import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  goodsRecognitions,
  type GoodsRecognitionItem,
} from '@/api/goodsWriteoffs';
import { orgs, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  warehouse_id: string;
  reason: string;
  comment: string;
  items: GoodsRecognitionItem[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  warehouse_id: '',
  reason: '',
  comment: '',
  items: [],
};

const newRow = (n: number): GoodsRecognitionItem => ({
  line_number: n,
  nomenclature_id: '',
  quantity: '1',
  unit_cost: '0',
});

const fmt = (n: number) =>
  n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function GoodsRecognitionEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по warehouse.inventory.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('warehouse.inventory.manage');
  const nomList = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.list({ limit: 500 }),
  });
  const docQuery = useQuery({
    queryKey: ['goods-recognition', id],
    queryFn: () => goodsRecognitions.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const numberPreview = useNumberPreview('doc.goods_recognitions', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);

  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refs.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f,
        organization_id: firstOrg,
        items: f.items.length ? f.items : [newRow(1)],
      });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        warehouse_id: d.warehouse_id,
        reason: d.reason ?? '',
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          characteristic_id: it.characteristic_id ?? null,
          series_id: it.series_id ?? null,
          quantity: String(it.quantity),
          unit_cost: String(it.unit_cost),
          amount: it.amount ? String(it.amount) : undefined,
        })),
      });
    }
  }, [isNew, docQuery.data, availableOrgs]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);
  const total = form.items.reduce(
    (s, e) => s + Number(e.quantity || 0) * Number(e.unit_cost || 0), 0,
  );

  const create = useMutation({
    mutationFn: () => goodsRecognitions.create({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      warehouse_id: form.warehouse_id,
      reason: form.reason || null,
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['goods-recognitions'] });
      navigate(`/goods-recognitions/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => goodsRecognitions.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      warehouse_id: form.warehouse_id,
      reason: form.reason || null,
      comment: form.comment || null,
      items: form.items,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['goods-recognition', id] }),
    onError: (e) => setError(extractError(e)),
  });
  // Новий: зберегти чернетку і одразу провести (create → post → картка).
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await goodsRecognitions.create({
        organization_id: form.organization_id,
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        warehouse_id: form.warehouse_id,
        reason: form.reason || null,
        comment: form.comment || null,
        items: form.items,
      });
      await goodsRecognitions.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['goods-recognitions'] });
      navigate(`/goods-recognitions/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Збережений: зберегти поточні правки і одразу провести (update → post).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await goodsRecognitions.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        warehouse_id: form.warehouse_id,
        reason: form.reason || null,
        comment: form.comment || null,
        items: form.items,
      });
      await goodsRecognitions.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-recognition', id] });
      qc.invalidateQueries({ queryKey: ['goods-recognitions'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => goodsRecognitions.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-recognition', id] });
      qc.invalidateQueries({ queryKey: ['goods-recognitions'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => goodsRecognitions.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-recognitions'] });
      navigate('/goods-recognitions');
    },
  });

  function setItem(i: number, patch: Partial<GoodsRecognitionItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => idx === i ? { ...e, ...patch } : e),
    }));
  }
  function addRow() {
    setForm((f) => ({ ...f, items: [...f.items, newRow(f.items.length + 1)] }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  // UX β: warning якщо новий документ а user не має warehouse.inventory.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: '#fef3c7', borderRadius: 6,
            border: '1px solid #fbbf24', color: '#92400e',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати оприбуткування товарів у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/goods-recognitions')}>Скасувати</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Нове оприбуткування' : `Оприбуткування: ${form.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ проведено
            </span>
          )}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/goods-recognitions')}
          onSave={() => isNew ? create.mutate() : update.mutate()}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={() => isNew ? createAndPost.mutate() : updateAndPost.mutate()}
          postDisabled={form.items.length === 0}
          postPending={create.isPending || createAndPost.isPending || updateAndPost.isPending || update.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm('Видалити документ?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Організація</label>
          <select value={form.organization_id}
                  onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
                  disabled={!isNew}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field">
          <label>№ документа</label>
          <input value={form.document_number}
                 onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>Дата</label>
          <DatePopover withTime value={form.document_date}
                 onChange={(v) => setForm({ ...form, document_date: v })}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>Склад</label>
          <select value={form.warehouse_id}
                  onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })}
                  disabled={isPosted}>
            <option value="">—</option>
            {warehousesQ.data?.map((w) => (
              <option key={w.id} value={w.id}>{w.code} — {w.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="field">
        <label>Причина (необов'язково)</label>
        <input value={form.reason}
               onChange={(e) => setForm({ ...form, reason: e.target.value })}
               disabled={isPosted}
               placeholder="напр., Знайдено на складі поза обліком" />
      </div>

      <h3 style={{ marginTop: '1.5rem' }}>Товари</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 30 }}>#</th>
            <th style={{ padding: '0.5rem' }}>Номенклатура</th>
            <th style={{ padding: '0.5rem', width: 110 }}>Кількість</th>
            <th style={{ padding: '0.5rem', width: 130 }}>Ціна (собівартість)</th>
            <th style={{ padding: '0.5rem', width: 130 }}>Сума</th>
            <th style={{ padding: '0.5rem', width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => {
            const amount = Number(it.quantity || 0) * Number(it.unit_cost || 0);
            return (
              <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
                <td style={{ padding: '0.5rem' }}>{it.line_number}</td>
                <td style={{ padding: '0.5rem' }}>
                  <select value={it.nomenclature_id}
                          onChange={(e) => setItem(i, { nomenclature_id: e.target.value })}
                          disabled={isPosted}
                          style={{ width: '100%' }}>
                    <option value="">—</option>
                    {nomList.data?.items.filter((n) => !n.is_service).map((n) => (
                      <option key={n.id} value={n.id}>{n.name}</option>
                    ))}
                  </select>
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <input type="number" step="0.0001" value={it.quantity}
                         onChange={(e) => setItem(i, { quantity: e.target.value })}
                         disabled={isPosted}
                         style={{ textAlign: 'right', width: '100%' }} />
                </td>
                <td style={{ padding: '0.5rem' }}>
                  <input type="number" step="0.01" value={it.unit_cost}
                         onChange={(e) => setItem(i, { unit_cost: e.target.value })}
                         disabled={isPosted}
                         style={{ textAlign: 'right', width: '100%' }} />
                </td>
                <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>
                  {fmt(amount)}
                </td>
                <td style={{ padding: '0.5rem' }}>
                  {!isPosted && (
                    <button onClick={() => removeRow(i)} className="danger"
                            style={{ padding: '0.3rem 0.6rem' }}>×</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 600 }}>
            <td colSpan={4} style={{ padding: '0.5rem', textAlign: 'right' }}>Усього:</td>
            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(total)}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
      {!isPosted && (
        <button onClick={addRow} style={{ marginTop: '0.75rem' }}>+ Додати рядок</button>
      )}

      <div className="field" style={{ marginTop: '1rem' }}>
        <label>Коментар</label>
        <input value={form.comment}
               onChange={(e) => setForm({ ...form, comment: e.target.value })}
               disabled={isPosted} />
      </div>

      {error && <div className="error">{error}</div>}

      {!isPosted && (
        <p style={{ marginTop: '1rem', color: '#64748b', fontSize: '0.85rem' }}>
          <strong>Як працює:</strong> при проведенні створюється нова партія товару
          зі вказаною собівартістю, рухи `+` у регістрах товарів на складі і партій,
          бухпроводка <strong>Дт 281 — Кт 719</strong> на суму = кількість × ціна.
        </p>
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}
